import {
    WithLoggedInAuthInfoProps,
    withRequiredAuthInfo,
} from "@propelauth/react";
import React, { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../ui/use-toast";
import { Button } from "../ui/button";
import { Card, CardHeader, CardContent, CardTitle } from "../ui/card";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "../ui/form";
import { AutosizeTextarea } from "../ui/autoresizeTextArea";
import { getQaItem, updateQAItem } from "./qa_api";
import { QAModel } from "./QAModel";
import BaseLoadingSpinner from "../ui/baseLoadingSpinner";

type EditReplyProps = WithLoggedInAuthInfoProps & {
    key?: string;
    reply_id: string;
    onSave?: () => void;
    onCancel: () => void;
};

const EditReply: React.FC<EditReplyProps> = (props) => {
    const { project_id } = useParams<{ project_id: string }>();
    const { reply_id, accessToken } = props;
    const { toast } = useToast();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = React.useState<boolean>(true);

    const form = useForm<QAModel>({
        defaultValues: {
            body: "",
        },
        mode: "onChange",
    });

    useEffect(() => {
        if (project_id && reply_id) {
            getQaItem(project_id, reply_id, accessToken)
                .then((data) => {
                    form.reset(data);
                    setIsLoading(false);
                })
                .catch((error) => {
                    console.error("Error fetching reply item:", error);
                    toast({
                        title: "Error",
                        description: "Failed to load the reply.",
                        variant: "destructive",
                    });
                    setIsLoading(false);
                });
        }
    }, [project_id, reply_id, accessToken, form, toast]);

    if (!project_id) {
        return <div>No ProjectID</div>;
    }
    if (isLoading) {
        return <div><BaseLoadingSpinner/></div>;
    }

    const onSave: SubmitHandler<QAModel> = (data: QAModel) => {
        updateQAItem(project_id, reply_id, accessToken, data)
            .then(() => {
                toast({
                    title: "Success!",
                    description: "Reply updated successfully.",
                    variant: "default",
                });
                if (props.onSave) {
                    props.onSave();
                } else {
                    navigate(-1); // Navigate back
                }
            })
            .catch((error) => {
                console.error("Error updating reply item:", error);
                toast({
                    title: "Error",
                    description: error.message || "Failed to update the reply.",
                    variant: "destructive",
                });
            });
    };

    return (
        <div className="container mb-5">
            <Card>
                <Form {...form}>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            form.handleSubmit(onSave)();
                        }}
                    >
                        <CardHeader>
                            <div className="flex justify-between items-center">
                                <Button
                                    onClick={() => props.onCancel()}
                                    type="button"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant={"secondary"}
                                    type="submit"
                                    disabled={!form.formState.isValid}
                                >
                                    Save
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <FormField
                                control={form.control}
                                name="body"
                                rules={{ required: "Reply body is required" }}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Reply</FormLabel>
                                        <FormControl>
                                            <AutosizeTextarea
                                                placeholder="Enter your reply"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </CardContent>
                    </form>
                </Form>
            </Card>
        </div>
    );
};

export default withRequiredAuthInfo(EditReply);
